/**
 * What a new queue needs, shared by "New queue" and "Send to annotation":
 * a name, the scores to ask (ticked from the shared list, in the order
 * ticked, or made on the spot; archived ones are not offered) and whom to
 * share it with.
 */

import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Field, Option } from "@/components/ui/field";
import { QueryState } from "@/components/ui/states";
import { ArchivedMatches } from "@/features/archive/archived";
import { EmailsInput } from "./emails-input";
import { useScoreConfigs } from "./hooks";
import { ScoreConfigForm } from "./score-config-form";
import { allowedAnswers, kindLabel } from "./scores";

export interface QueueDraft {
  name: string;
  description: string;
  scoreIds: string[];
  reviewers: string[];
}

export const emptyDraft = (name = ""): QueueDraft => ({ name, description: "", scoreIds: [], reviewers: [] });

export const draftReady = (d: QueueDraft) => d.name.trim() !== "" && d.scoreIds.length > 0;

export const toCreate = (d: QueueDraft) => ({
  name: d.name.trim(),
  description: d.description.trim(),
  score_config_ids: d.scoreIds,
  reviewers: d.reviewers,
});

function ScorePicker({ value, onChange }: { value: string[]; onChange: (ids: string[]) => void }) {
  const configs = useScoreConfigs();
  const [making, setMaking] = useState(false);
  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="pb-1.5 text-[13px] font-semibold">
        Scores to ask
        <span aria-hidden className="pl-0.5 text-err after:content-['*']" />
      </legend>
      <QueryState query={configs} what="scores">
        {configs.data?.length ? (
          <div className="flex max-h-56 flex-col gap-2 overflow-y-auto rounded-lg border border-line-soft bg-well p-3">
            {configs.data.map((c) => (
              <Option
                key={c.id}
                type="checkbox"
                checked={value.includes(c.id)}
                onChange={() => toggle(c.id)}
                note={`${kindLabel(c)} · ${allowedAnswers(c)}`}
              >
                {c.name}
              </Option>
            ))}
          </div>
        ) : (
          !making && <p className="m-0 text-xs text-muted">No scores yet. Make the first one.</p>
        )}
      </QueryState>
      <p className="m-0 empty:hidden">
        <ArchivedMatches kind="scoreConfig" />
      </p>
      {making ? (
        <div className="rounded-lg border border-line-soft p-3">
          <ScoreConfigForm
            onCreated={(c) => (onChange([...value, c.id]), setMaking(false))}
            onCancel={() => setMaking(false)}
          />
        </div>
      ) : (
        <Button size="sm" className="self-start" onClick={() => setMaking(true)}>
          <Plus size={13} strokeWidth={2.2} aria-hidden />
          New score
        </Button>
      )}
    </fieldset>
  );
}

export function QueueFields({
  draft,
  onChange,
  withDescription = true,
}: {
  draft: QueueDraft;
  onChange: (patch: Partial<QueueDraft>) => void;
  withDescription?: boolean;
}) {
  return (
    <>
      <Field label="Name" required htmlFor="queue-name">
        <input
          id="queue-name"
          className="inp"
          placeholder="e.g. CT chest · inclusion review"
          value={draft.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
      </Field>
      {withDescription && (
        <Field
          label="Instructions"
          optional
          htmlFor="queue-description"
          hint="Shown to reviewers above every item."
        >
          <textarea
            id="queue-description"
            className="inp min-h-[64px]!"
            placeholder="What reviewers should look for"
            value={draft.description}
            onChange={(e) => onChange({ description: e.target.value })}
          />
        </Field>
      )}
      <ScorePicker value={draft.scoreIds} onChange={(scoreIds) => onChange({ scoreIds })} />
      <EmailsInput value={draft.reviewers} onChange={(reviewers) => onChange({ reviewers })} />
    </>
  );
}
